import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Event as EventIcon,
  EmojiEvents as TrophyIcon,
  AssignmentTurnedIn as AssignmentIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  School as SchoolIcon,
  Timer as TimerIcon,
  Info as InfoIcon,
  Psychology as PsychologyIcon,
  Whatshot as WhatshotIcon,
  Stars as StarsIcon,
  CalendarMonth as CalendarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import GuardiansCard from '../../components/student/GuardiansCard';
import LiveQuizBanner from '../../components/student/LiveQuizBanner';
import { getUserAchievements } from '../../services/achievementService';
import { getEnrollmentsByUser } from '../../services/enrollmentService';
import { getUserSubmissions } from '../../services/problemService';
import { getProblemsBySession } from '../../services/problemService';
import { getSessionsAcceptingEnrollment } from '../../services/sessionService';
import { getLeaderboard } from '../../services/userService';
import { StudentAchievement } from '../../types/achievement';
import { Session } from '../../services/sessionService';

interface ProcessedSession extends Session {
  status: 'active' | 'upcoming' | 'past';
  progress: number;
  problemsSolved: number;
  totalProblems: number;
  enrollmentDate: string;
}

function DashboardPage() {
  const { authState } = useAuth();
  const { user } = authState;

  // State for data
  const [recentAchievements, setRecentAchievements] = useState<StudentAchievement[]>([]);
  const [loadingAchievements, setLoadingAchievements] = useState<boolean>(true);
  const [activeSessions, setActiveSessions] = useState<ProcessedSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState<boolean>(true);
  const [availableSessions, setAvailableSessions] = useState<Session[]>([]);
  const [loadingAvailableSessions, setLoadingAvailableSessions] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [problemStats, setProblemStats] = useState<{
    totalSolved: number;
    correctRate: number;
    totalPoints: number;
    rank: number;
    nextRankPoints: number;
    rankProgress: number;
  }>({
    totalSolved: 0,
    correctRate: 0,
    totalPoints: 0,
    rank: 0,
    nextRankPoints: 0,
    rankProgress: 0
  });

  // Fetch user enrollments and sessions
  useEffect(() => {
    const fetchUserEnrollments = async () => {
      if (!user) return;
      
      try {
        setLoadingSessions(true);
        
        // Get all enrollments for the current user
        const enrollmentsData = await getEnrollmentsByUser(user.id);

        if (!enrollmentsData || !enrollmentsData.enrollments) {
          setActiveSessions([]);
          setLoadingSessions(false);
          return;
        }
        
        // Get all user submissions
        const submissions = await getUserSubmissions();

        // The backend null-fills `session` on an enrollment when that
        // session isn't visible to the caller's tenant. Drop those before
        // processing so one hidden session doesn't blank the whole list.
        const visibleEnrollments = enrollmentsData.enrollments.filter((enrollment) => {
          if (!enrollment.session) {
            console.warn(`Skipping enrollment ${enrollment.id}: session is not visible to this tenant`);
            return false;
          }
          return true;
        });

        // Process each session
        const processedSessions = await Promise.all(
          visibleEnrollments.map(async (enrollment) => {
            const session = enrollment.session;
            
            // Include all sessions but categorize them by status
            const now = new Date();
            const startDate = new Date(session.startDate);
            const endDate = new Date(session.endDate);
            
            // Determine session status based on dates (date-only comparison)
            const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
            const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
            
            let status: 'active' | 'upcoming' | 'past';
            if (nowDateOnly >= startDateOnly && nowDateOnly <= endDateOnly) {
              status = 'active';
            } else if (nowDateOnly < startDateOnly) {
              status = 'upcoming';
            } else {
              status = 'past';
            }

            // Get all problems for this session
            const problems = await getProblemsBySession(session.id);
            
            // Calculate how many problems the user has solved in this session
            const sessionSubmissions = submissions.filter(sub => 
              problems.some(prob => prob.id === sub.problemId)
            );
            
            const problemsSolved = sessionSubmissions.length;
            const totalProblems = problems.length;
            const progress = totalProblems > 0 ? Math.round((problemsSolved / totalProblems) * 100) : 0;
            
            return {
              ...session,
              status,
              progress,
              problemsSolved,
              totalProblems,
              enrollmentDate: enrollment.enrollmentDate
            };
          })
        );
        
        // Now processedSessions contains all sessions with their status
        // First, group sessions by status
        const groupedSessions = processedSessions
          .reduce((groups: { active: ProcessedSession[], upcoming: ProcessedSession[], past: ProcessedSession[] }, session) => {
            groups[session.status].push(session);
            return groups;
          }, { active: [], upcoming: [], past: [] });
        
        // Sort each group appropriately
        groupedSessions.active.sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime()); // Soonest ending first
        groupedSessions.upcoming.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()); // Soonest starting first
        groupedSessions.past.sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()); // Most recently ended first
        
        // Combine groups in order: active, upcoming, past
        const allSessions = [
          ...groupedSessions.active,
          ...groupedSessions.upcoming,
          ...groupedSessions.past
        ];

        setActiveSessions(allSessions);
        
        // Calculate overall problem stats
        if (submissions.length > 0) {
          const totalSolved = submissions.length;
          const correctCount = submissions.filter(sub => sub.isCorrect).length;
          const correctRate = Math.round((correctCount / totalSolved) * 100);
          
          // Get actual points from leaderboard API instead of calculating manually
          try {
            const leaderboardData = await getLeaderboard(1, 1000); // Get all users to find current user
            const currentUserData = leaderboardData.users?.find((u) => u.id === user.id);
            
            const points = currentUserData?.totalPoints || 0;
            
            // Define rank thresholds - each rank requires progressively more points
            const rankThresholds = [0, 10, 25, 50, 100, 200, 300, 500, 750, 1000];
            
            // Find the current rank (0-based index in the thresholds array)
            let rank = 0;
            for (let i = 0; i < rankThresholds.length; i++) {
              if (points >= rankThresholds[i]) {
                rank = i;
              } else {
                break;
              }
            }
            
            // Next rank points needed
            const nextRankPoints = rank < rankThresholds.length - 1 
              ? Math.round((rankThresholds[rank + 1] - points) * 100) / 100 // Round to 2 decimals
              : 0;
            
            // Calculate progress percentage to next rank
            const currentThreshold = rankThresholds[rank];
            const nextThreshold = rank < rankThresholds.length - 1 
              ? rankThresholds[rank + 1] 
              : currentThreshold;
            
            const rankProgress = nextThreshold > currentThreshold
              ? Math.min(100, Math.round(((points - currentThreshold) / (nextThreshold - currentThreshold)) * 100))
              : 100;
            
            setProblemStats({
              totalSolved,
              correctRate,
              totalPoints: points,
              rank: rank + 1, // Display as 1-based rank
              nextRankPoints,
              rankProgress
            });
          } catch (error) {
            console.error('Error fetching leaderboard data:', error);
            // Fallback to simple calculation without points
            setProblemStats({
              totalSolved,
              correctRate,
              totalPoints: 0,
              rank: 0,
              nextRankPoints: 0,
              rankProgress: 0
            });
          }
        }
      } catch (err) {
        console.error("Error fetching user enrollments:", err);
        setError("Failed to load your sessions. Please try refreshing the page.");
      } finally {
        setLoadingSessions(false);
      }
    };
    
    fetchUserEnrollments();
  }, [user]);

  // Fetch user achievements
  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        setLoadingAchievements(true);
        const achievements = await getUserAchievements();
        
        // Sort by date and take the most recent 3
        const sorted = achievements
          .sort((a, b) => new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime())
          .slice(0, 3);
        
        setRecentAchievements(sorted);
      } catch (error) {
        console.error("Error fetching achievements:", error);
      } finally {
        setLoadingAchievements(false);
      }
    };
    
    fetchAchievements();
  }, []);

  // Helper function to get icon based on achievement iconName
  const getAchievementIcon = (iconName?: string) => {
    const icons: { [key: string]: React.ReactNode } = {
      'EmojiEvents': <TrophyIcon color="primary" />,
      'School': <SchoolIcon color="primary" />,
      'Psychology': <PsychologyIcon color="primary" />,
      'Whatshot': <WhatshotIcon color="primary" />,
      'Stars': <StarsIcon color="primary" />,
      'Timer': <TimerIcon color="primary" />,
      'CalendarMonth': <CalendarIcon color="primary" />,
      'AssignmentTurnedIn': <AssignmentIcon color="primary" />
    };
    
    return icons[iconName || ''] || <StarIcon color="primary" />;
  };
  
  // Helper function to get the correct suffix for a rank number (1st, 2nd, 3rd, etc.)
  const getRankSuffix = (rank: number): string => {
    if (rank >= 11 && rank <= 13) {
      return 'th';
    }
    
    switch (rank % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  // Fetch available sessions (sessions accepting enrollment that the user is not enrolled in)
  useEffect(() => {
    const fetchAvailableSessions = async () => {
      if (!user) return;
      
      try {
        setLoadingAvailableSessions(true);

        // Get all sessions accepting enrollment
        const enrollmentSessions = await getSessionsAcceptingEnrollment();

        // Get user's enrolled session IDs
        const enrollmentsData = await getEnrollmentsByUser(user.id);
        const enrolledSessionIds = enrollmentsData?.enrollments?.map(e => e.sessionId) || [];

        // Filter to only include sessions the user is not already enrolled in and that haven't ended
        const now = new Date();
        const availableToEnroll = enrollmentSessions.filter(
          session => {
            const hasNotEnded = new Date(session.endDate) >= now;
            const notEnrolled = !enrolledSessionIds.includes(session.id);
            return hasNotEnded && notEnrolled;
          }
        );

        setAvailableSessions(availableToEnroll);
      } catch (err) {
        console.error('Error fetching available sessions:', err);
      } finally {
        setLoadingAvailableSessions(false);
      }
    };
    
    fetchAvailableSessions();
  }, [user]);

  return (
    <MainLayout>
      <Box sx={{ mb: 4 }} data-demo-id="student-welcome">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 500,
                color: 'text.primary',
                letterSpacing: '-0.01em',
                fontSize: '1.5rem',
                lineHeight: 1.3
              }}
            >
              Welcome, {user?.firstName}!
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontSize: '0.95rem',
                maxWidth: '90%'
              }}
            >
              Here's your current progress and upcoming events.
            </Typography>
          </Box>
        </Box>
      </Box>

      <LiveQuizBanner />

      {/* Main Content */}
      <Grid container spacing={4}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
            <CardHeader 
              title="My Sessions" 
              action={
                <Button 
                  variant="contained" 
                  component={RouterLink}
                  to="/browse-sessions"
                  size="small"
                >
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              
              {loadingSessions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : activeSessions.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    You're not enrolled in any active sessions.
                  </Typography>
                  <Button 
                    variant="outlined"
                    component={RouterLink}
                    to="/browse-sessions"
                    sx={{ mt: 2 }}
                  >
                    Browse Available Sessions
                  </Button>
                </Box>
              ) : (
                activeSessions.map((session) => (
                  <Paper 
                    key={session.id} 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      borderRadius: 2,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {session.title}
                      </Typography>
                      {session.status === 'active' && (
                        <Chip 
                          size="small"
                          icon={<SchoolIcon />}
                          label="Active" 
                          color="success"
                        />
                      )}
                      {session.status === 'upcoming' && (
                        <Chip 
                          size="small"
                          icon={<EventIcon />}
                          label="Upcoming" 
                          color="primary"
                        />
                      )}
                      {session.status === 'past' && (
                        <Chip 
                          size="small"
                          icon={<AssignmentIcon />}
                          label="Completed" 
                          color="default"
                        />
                      )}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {`${new Date(session.startDate).toLocaleDateString()} - ${new Date(session.endDate).toLocaleDateString()}`}
                    </Typography>
                    
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        Progress: {session.progress}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={session.progress} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Typography variant="body2">
                        {session.problemsSolved} of {session.totalProblems} problems solved
                      </Typography>
                      
                      {session.status === 'active' && (
                        <Button 
                          variant="contained" 
                          size="small"
                          component={RouterLink}
                          to={`/sessions/${session.id}/problems`}
                          endIcon={<ArrowForwardIcon />}
                        >
                          {session.progress === 0 ? 'Start' : 'Continue'}
                        </Button>
                      )}
                      
                      {session.status === 'upcoming' && (
                        <Button 
                          variant="outlined" 
                          size="small"
                          disabled
                          startIcon={<EventIcon />}
                        >
                          Starts {new Date(session.startDate).toLocaleDateString()}
                        </Button>
                      )}
                      
                      {session.status === 'past' && (
                        <Button
                          variant="outlined"
                          size="small"
                          component={RouterLink}
                          to={`/sessions/${session.id}/stats`}
                          endIcon={<AssignmentIcon />}
                        >
                          View Results
                        </Button>
                      )}
                    </Box>
                  </Paper>
                ))
              )}
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardHeader 
              title="Available Sessions" 
              action={
                <Button 
                  variant="text" 
                  component={RouterLink}
                  to="/browse-sessions"
                  size="small"
                >
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {loadingAvailableSessions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : availableSessions.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No new sessions available for enrollment at this time.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {availableSessions.slice(0, 3).map((session) => (
                    <Paper 
                      key={session.id} 
                      elevation={1}
                      sx={{ 
                        p: 2, 
                        mb: 2, 
                        borderRadius: 2,
                        '&:last-child': { mb: 0 }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                          {session.title}
                        </Typography>
                        <Chip 
                          size="small"
                          icon={<EventIcon />}
                          label="Open" 
                          color="success"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {`${new Date(session.startDate).toLocaleDateString()} - ${new Date(session.endDate).toLocaleDateString()}`}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 2 }} noWrap>
                        {session.description}
                      </Typography>
                      
                      <Button 
                        variant="outlined" 
                        fullWidth
                        component={RouterLink}
                        to={`/sessions/${session.id}/enroll`}
                        sx={{ mt: 1 }}
                      >
                        Enroll Now
                      </Button>
                    </Paper>
                  ))}
                </List>
              )}
              
              {availableSessions.length > 3 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button 
                    component={RouterLink} 
                    to="/browse-sessions"
                    endIcon={<ArrowForwardIcon />}
                  >
                    See {availableSessions.length - 3} More
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }} data-demo-id="student-stats-card">
            <CardHeader
              title="My Stats"
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.primary.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <TrophyIcon sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{problemStats.totalSolved}</Typography>
                    <Typography variant="body2" color="text.secondary">Problems Solved</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.success.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <TrendingUpIcon sx={{ color: 'success.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{problemStats.totalPoints.toFixed(2)}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Points</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: (theme) => `${theme.palette.secondary.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <StarIcon sx={{ color: 'secondary.main', fontSize: 32, mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{problemStats.correctRate}%</Typography>
                    <Typography variant="body2" color="text.secondary">Accuracy Rate</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      mt: 1,
                      bgcolor: (theme) => `${theme.palette.warning.main}10`,
                      borderRadius: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SchoolIcon sx={{ color: 'warning.main', mr: 1 }} />
                      <Typography variant="body1" fontWeight="medium">
                        Current Rank: {problemStats.rank === 0 ? "Unranked" : 
                          problemStats.rank === 10 ? "Grandmaster" : 
                          `${problemStats.rank}${getRankSuffix(problemStats.rank)}`}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={problemStats.rankProgress} 
                      color="secondary"
                      sx={{ height: 8, borderRadius: 4, mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {problemStats.rank < 10 ? 
                        `${problemStats.nextRankPoints} more points to reach the next rank` : 
                        "Maximum rank achieved!"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button 
                  fullWidth 
                  variant="outlined"
                  component={RouterLink}
                  to="/student/achievements"
                >
                  View My Achievements
                </Button>
              </Box>
            </CardContent>
          </Card>

          <GuardiansCard />

          <Card elevation={2} sx={{ mb: 4, borderRadius: 2, mt: 4 }}>
            <CardHeader
              title="Recent Achievements" 
            />
            <Divider />
            <CardContent sx={{ px: 0 }}>
              {loadingAchievements ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : recentAchievements.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center', px: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    You haven't earned any achievements yet.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Start solving problems to earn achievements!
                  </Typography>
                </Box>
              ) : (
                <List>
                  {recentAchievements.map((achievement) => (
                    <React.Fragment key={achievement.id}>
                      <ListItem>
                        <ListItemIcon>
                          {achievement.achievement ? 
                            getAchievementIcon(achievement.achievement.iconName) : 
                            <TrophyIcon color="primary" />
                          }
                        </ListItemIcon>
                        <ListItemText
                          primary={achievement.achievement?.name || 'Achievement'}
                          secondary={
                            <>
                              <Typography variant="body2">
                                {achievement.achievement?.description || 'Great work!'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(achievement.awardedAt).toLocaleDateString()}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  component={RouterLink} 
                  to="/student/achievements"
                  endIcon={<ArrowForwardIcon />}
                >
                  View All Achievements
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardHeader 
              title="Need Help?" 
              avatar={<InfoIcon color="primary" />}
            />
            <Divider />
            <CardContent>
              <Typography variant="body2" paragraph>
                If you need assistance with problems or have questions about the platform, we're here to help!
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button 
                  variant="contained" 
                  fullWidth
                  component={RouterLink}
                  to="/help"
                  sx={{ mb: 1 }}
                >
                  View Help Resources
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth
                  component={RouterLink}
                  to="/contact"
                >
                  Contact Support
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </MainLayout>
  );
}

export default DashboardPage;